import { IDB_filesMeta, IDB_guides, IDB_reportGuideDetails, IDB_syncParamsGuides } from '../IDB/IDB.dexie'
import { Sync_standardSync, type Sync_Params } from '../Sync/Sync.standardSync'
import { Serv_clientWithLogger } from '../Serv/Serv.client'
import type { FileMetaApp } from '../../../shared/domain/files/types.no-deps'
import type { ReportGuideDetails, SyncGuide } from '../../../shared/domain/guides'
import type { ClientLogger } from '../../../shared/lib/ClientLogger.no-deps'
import { compact, uniq } from '../../../shared/lib/functional/array.no-deps'
import { pipe } from '../../../shared/lib/functional/pipe.no-deps'

interface SyncResponse {
    fileMetas: FileMetaApp[]
    guides: SyncGuide[]
    reportGuideDetails: ReportGuideDetails[]
    syncTime: Date
    teams: number[]
}

export async function Guides_sync(logger: ClientLogger) {
    logger.log(`syncing guides`)

    await Sync_standardSync({
        params: IDB_syncParamsGuides(),
        sync: (params) => sync(logger, params),
        write: (response: SyncResponse) => write(logger, response),
        clear: () => IDB_guides().clear(),
    })

    logger.log(`syncing guides done`)
}

async function sync(logger: ClientLogger, params: Sync_Params): Promise<SyncResponse> {
    const apiClient = Serv_clientWithLogger(logger)

    const { guides, syncTime } = await apiClient.getAllGuides({ lastSyncTime: params.lastSyncTime })

    // Since guides can be files or report guides with an image, or vice-versa, we will need to fetch file meta info as well.
    const fileIds = guides.filter((guide) => guide.variant === 'file').map((guide) => guide.file)

    // Sync report guide details for report guides.
    const reportGuideDetails: ReportGuideDetails[] = []
    const reportGuideIds = guides.filter((guide) => guide.variant === 'report').map((guide) => guide.report)
    if (reportGuideIds.length > 0) {
        const { reportGuideDetails: updatedDetails } = await apiClient.getReportGuideDetailsBulk({
            ids: reportGuideIds,
            lastSyncTime: params.lastSyncTime,
        })

        reportGuideDetails.push(...updatedDetails)

        // Get a list of first images in report guides
        const reportGuideFirstImages = updatedDetails.map((reportGuideDetail) => reportGuideDetail?.firstImage)

        // Add report guides first images in files ids so that file meta can be fetched for them
        fileIds.push(...pipe(reportGuideFirstImages, compact, uniq))
    }

    const fileMetas: FileMetaApp[] = []
    if (fileIds.length > 0) {
        const { fileMeta: updatedMetas } = await apiClient.getFileMetaBulk({ fileIds })
        fileMetas.push(...updatedMetas)
    }

    if (guides.length > 0) {
        logger.log(`downloaded ${guides.length} guides`)
    }

    if (reportGuideDetails.length > 0) {
        logger.log(`downloaded ${reportGuideDetails.length} report guide details`)
    }

    if (fileMetas.length > 0) {
        logger.log(`downloaded ${fileMetas.length} file metas`)
    }

    return { fileMetas, guides, reportGuideDetails, syncTime, teams: params.teams }
}

async function write(logger: ClientLogger, response: SyncResponse) {
    const fileMetaCount = response.fileMetas.length
    const guideCount = response.guides.length
    const reportGuideDetailsCount = response.reportGuideDetails.length

    if (guideCount > 0) {
        logger.log(`saving guides in idb`)
        await IDB_guides().bulkPut(response.guides)
        logger.log(`saved ${guideCount} guides in idb`)
    }

    if (fileMetaCount > 0) {
        logger.log(`saving file metas in idb`)
        await IDB_filesMeta().bulkPut(response.fileMetas)
        logger.log(`saved ${fileMetaCount} file metas in idb`)
    }

    if (reportGuideDetailsCount > 0) {
        logger.log(`saving report guide details in idb`)
        await IDB_reportGuideDetails().bulkPut(response.reportGuideDetails)
        logger.log(`saved ${reportGuideDetailsCount} report guide details in idb`)
    }
}
